import { assertMapShellProjectionV1 } from "./map-shell-projection.js";
import {
  FISHYMAP_POINT_ICON_SCALE_MAX,
  FISHYMAP_POINT_ICON_SCALE_MIN,
} from "./map-bridge-contract.js";
import { normalizeVisibleTradeNpcClasses } from "./map-trade-npc-contract.js";

export const FISHYMAP_LAYER_FILTER_CHANGE_EVENT = "fishymap:layer-filter-change";
export const FISHYMAP_LAYER_PRESENTATION_CHANGE_EVENT =
  "fishymap:layer-presentation-change";
export const FISHYMAP_WINDOW_LAYOUT_CHANGE_EVENT = "fishymap:window-layout-change";

const CONTRACT_VERSION = 1;
const MAX_IDENTIFIER_LENGTH = 256;
const MAX_COLLECTION_LENGTH = 8_192;
const WINDOW_IDS = new Set([
  "search",
  "help",
  "bookmarks",
  "settings",
  "zoneInfo",
  "layers",
]);
const BOOLEAN_LAYER_FILTER_KINDS = new Set([
  "waypoint-connections",
  "waypoint-labels",
  "point-icons",
  "point-clustering",
  "confidence-showcase",
]);

function isPlainObject(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function assertExactKeys(value, expectedKeys, label) {
  if (!isPlainObject(value)) {
    throw new TypeError(`${label} must be a plain object`);
  }
  const expected = new Set(expectedKeys);
  for (const key of Object.keys(value)) {
    if (!expected.has(key)) {
      throw new TypeError(`${label} has unsupported field: ${key}`);
    }
  }
  for (const key of expected) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) {
      throw new TypeError(`${label} requires field: ${key}`);
    }
  }
}

function assertVersion(value, label) {
  if (value.version !== CONTRACT_VERSION) {
    throw new TypeError(`${label} has an unsupported version`);
  }
}

function normalizedIdentifier(value, label) {
  if (
    typeof value !== "string"
    || !value
    || value !== value.trim()
    || value.length > MAX_IDENTIFIER_LENGTH
  ) {
    throw new TypeError(`${label} must be a non-empty normalized string`);
  }
  return value;
}

function normalizedIdentifierList(value, label) {
  if (!Array.isArray(value) || value.length > MAX_COLLECTION_LENGTH) {
    throw new TypeError(`${label} must be a bounded array`);
  }
  const result = [];
  const seen = new Set();
  for (const entry of value) {
    const identifier = normalizedIdentifier(entry, `${label} entry`);
    if (seen.has(identifier)) {
      throw new TypeError(`${label} must not contain duplicate identifiers`);
    }
    seen.add(identifier);
    result.push(identifier);
  }
  return Object.freeze(result);
}

function boundedNumber(value, minimum, maximum, label) {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new TypeError(`${label} must be a finite number`);
  }
  if (value < minimum || value > maximum) {
    throw new TypeError(`${label} is outside its supported range`);
  }
  return value;
}

function normalizeBooleanOverride(value, label) {
  if (value !== null && typeof value !== "boolean") {
    throw new TypeError(`${label} must be boolean or null`);
  }
  return value;
}

function normalizeNumberOverride(value, minimum, maximum, label) {
  return value === null ? null : boundedNumber(value, minimum, maximum, label);
}

function normalizeHighlightOverride(value) {
  if (value === null) {
    return null;
  }
  assertExactKeys(
    value,
    ["enabled", "opacity", "color"],
    "Layer highlight override",
  );
  if (typeof value.enabled !== "boolean") {
    throw new TypeError("Layer highlight enabled must be boolean");
  }
  const opacity = boundedNumber(value.opacity, 0, 1, "Layer highlight opacity");
  if (
    !Array.isArray(value.color)
    || value.color.length !== 3
    || value.color.some(
      (channel) => !Number.isInteger(channel) || channel < 0 || channel > 255,
    )
  ) {
    throw new TypeError("Layer highlight color must contain exactly three bytes");
  }
  return Object.freeze({
    enabled: value.enabled,
    opacity,
    color: Object.freeze(value.color.slice()),
  });
}

function normalizeClipMaskByLayer(value, orderedLayerIds) {
  if (!isPlainObject(value)) {
    throw new TypeError("Layer clip masks must be a plain object");
  }
  const entries = Object.entries(value);
  if (entries.length > MAX_COLLECTION_LENGTH) {
    throw new TypeError("Layer clip masks exceed the supported size");
  }
  const knownLayerIds = new Set(orderedLayerIds);
  const result = {};
  for (const [rawLayerId, rawMaskLayerId] of entries) {
    const layerId = normalizedIdentifier(rawLayerId, "Layer clip-mask key");
    const maskLayerId = normalizedIdentifier(rawMaskLayerId, "Layer clip-mask value");
    if (!knownLayerIds.has(layerId) || !knownLayerIds.has(maskLayerId)) {
      throw new TypeError("Layer clip masks must reference ordered layer identifiers");
    }
    if (layerId === maskLayerId) {
      throw new TypeError("Layer clip masks must not reference themselves");
    }
    result[layerId] = maskLayerId;
  }
  for (const layerId of Object.keys(result)) {
    const seen = new Set([layerId]);
    let cursor = result[layerId];
    while (cursor && result[cursor]) {
      if (seen.has(cursor)) {
        throw new TypeError("Layer clip masks must be acyclic");
      }
      seen.add(cursor);
      cursor = result[cursor];
    }
  }
  return Object.freeze(result);
}

function normalizeVisibleFacts(value) {
  if (!isPlainObject(value)) {
    throw new TypeError("Layer hover facts must be a plain object");
  }
  const entries = Object.entries(value);
  if (entries.length > MAX_COLLECTION_LENGTH) {
    throw new TypeError("Layer hover facts exceed the supported size");
  }
  const result = {};
  for (const [rawFactKey, visible] of entries) {
    const factKey = normalizedIdentifier(rawFactKey, "Layer hover-fact key");
    if (typeof visible !== "boolean") {
      throw new TypeError("Layer hover-fact visibility must be boolean");
    }
    result[factKey] = visible;
  }
  return Object.freeze(result);
}

export function normalizeMapLayerFilterChangeV1(value) {
  if (!isPlainObject(value)) {
    throw new TypeError("Map layer filter change must be a plain object");
  }
  assertVersion(value, "Map layer filter change");
  switch (value.kind) {
    case "visibility":
      assertExactKeys(
        value,
        ["version", "kind", "visibleLayerIds"],
        "Map layer visibility change",
      );
      return Object.freeze({
        version: CONTRACT_VERSION,
        kind: value.kind,
        visibleLayerIds: normalizedIdentifierList(
          value.visibleLayerIds,
          "Visible layer identifiers",
        ),
      });
    case "stack": {
      assertExactKeys(
        value,
        ["version", "kind", "orderedLayerIds", "clipMaskByLayer"],
        "Map layer stack change",
      );
      const orderedLayerIds = normalizedIdentifierList(
        value.orderedLayerIds,
        "Ordered layer identifiers",
      );
      return Object.freeze({
        version: CONTRACT_VERSION,
        kind: value.kind,
        orderedLayerIds,
        clipMaskByLayer: normalizeClipMaskByLayer(
          value.clipMaskByLayer,
          orderedLayerIds,
        ),
      });
    }
    case "opacity":
      assertExactKeys(
        value,
        ["version", "kind", "layerId", "override"],
        "Map layer opacity change",
      );
      return Object.freeze({
        version: CONTRACT_VERSION,
        kind: value.kind,
        layerId: normalizedIdentifier(value.layerId, "Layer identifier"),
        override: normalizeNumberOverride(value.override, 0, 1, "Layer opacity"),
      });
    case "point-icon-scale":
      assertExactKeys(
        value,
        ["version", "kind", "layerId", "override"],
        "Map layer point-icon scale change",
      );
      return Object.freeze({
        version: CONTRACT_VERSION,
        kind: value.kind,
        layerId: normalizedIdentifier(value.layerId, "Layer identifier"),
        override: normalizeNumberOverride(
          value.override,
          FISHYMAP_POINT_ICON_SCALE_MIN,
          FISHYMAP_POINT_ICON_SCALE_MAX,
          "Layer point-icon scale",
        ),
      });
    case "trade-npc-classes": {
      assertExactKeys(
        value,
        ["version", "kind", "classesVisible"],
        "Trade NPC class filter change",
      );
      const identifiers = normalizedIdentifierList(
        value.classesVisible,
        "Visible Trade NPC classes",
      );
      const classesVisible = normalizeVisibleTradeNpcClasses(identifiers);
      if (classesVisible.length !== identifiers.length) {
        throw new TypeError("Visible Trade NPC classes contain an unsupported class");
      }
      return Object.freeze({
        version: CONTRACT_VERSION,
        kind: value.kind,
        classesVisible: Object.freeze(classesVisible),
      });
    }
    case "highlight":
      assertExactKeys(
        value,
        ["version", "kind", "layerId", "override"],
        "Map layer highlight change",
      );
      return Object.freeze({
        version: CONTRACT_VERSION,
        kind: value.kind,
        layerId: normalizedIdentifier(value.layerId, "Layer identifier"),
        override: normalizeHighlightOverride(value.override),
      });
    default:
      if (!BOOLEAN_LAYER_FILTER_KINDS.has(value.kind)) {
        throw new TypeError("Map layer filter change has an unsupported kind");
      }
      assertExactKeys(
        value,
        ["version", "kind", "layerId", "override"],
        "Map layer boolean filter change",
      );
      return Object.freeze({
        version: CONTRACT_VERSION,
        kind: value.kind,
        layerId: normalizedIdentifier(value.layerId, "Layer identifier"),
        override: normalizeBooleanOverride(value.override, "Layer filter override"),
      });
  }
}

export function normalizeMapLayerPresentationChangeV1(value) {
  if (!isPlainObject(value)) {
    throw new TypeError("Map layer presentation change must be a plain object");
  }
  assertVersion(value, "Map layer presentation change");
  switch (value.kind) {
    case "expanded-settings":
      assertExactKeys(
        value,
        ["version", "kind", "expandedLayerIds"],
        "Map expanded-layer settings change",
      );
      return Object.freeze({
        version: CONTRACT_VERSION,
        kind: value.kind,
        expandedLayerIds: normalizedIdentifierList(
          value.expandedLayerIds,
          "Expanded layer identifiers",
        ),
      });
    case "lineage":
    case "sample-hover":
      assertExactKeys(
        value,
        ["version", "kind", "layerId", "visible"],
        "Map layer visibility presentation change",
      );
      if (typeof value.visible !== "boolean") {
        throw new TypeError("Map layer presentation visibility must be boolean");
      }
      return Object.freeze({
        version: CONTRACT_VERSION,
        kind: value.kind,
        layerId: normalizedIdentifier(value.layerId, "Layer identifier"),
        visible: value.visible,
      });
    case "hover-facts":
      assertExactKeys(
        value,
        ["version", "kind", "layerId", "visibleFacts"],
        "Map layer hover-facts presentation change",
      );
      return Object.freeze({
        version: CONTRACT_VERSION,
        kind: value.kind,
        layerId: normalizedIdentifier(value.layerId, "Layer identifier"),
        visibleFacts: normalizeVisibleFacts(value.visibleFacts),
      });
    default:
      throw new TypeError("Map layer presentation change has an unsupported kind");
  }
}

export function normalizeMapWindowLayoutChangeV1(value) {
  if (!isPlainObject(value)) {
    throw new TypeError("Map window layout change must be a plain object");
  }
  assertVersion(value, "Map window layout change");
  const windowId = normalizedIdentifier(value.windowId, "Map window identifier");
  if (!WINDOW_IDS.has(windowId)) {
    throw new TypeError("Map window layout change has an unsupported window identifier");
  }
  switch (value.kind) {
    case "open":
      assertExactKeys(
        value,
        ["version", "kind", "windowId", "open"],
        "Map window open change",
      );
      if (typeof value.open !== "boolean") {
        throw new TypeError("Map window open state must be boolean");
      }
      return Object.freeze({ version: CONTRACT_VERSION, kind: value.kind, windowId, open: value.open });
    case "collapsed":
      assertExactKeys(
        value,
        ["version", "kind", "windowId", "collapsed"],
        "Map window collapsed change",
      );
      if (windowId === "search") {
        throw new TypeError("The search window cannot be collapsed");
      }
      if (typeof value.collapsed !== "boolean") {
        throw new TypeError("Map window collapsed state must be boolean");
      }
      return Object.freeze({
        version: CONTRACT_VERSION,
        kind: value.kind,
        windowId,
        collapsed: value.collapsed,
      });
    case "position": {
      assertExactKeys(
        value,
        ["version", "kind", "windowId", "x", "y"],
        "Map window position change",
      );
      if (!Number.isSafeInteger(value.x) || value.x < 0) {
        throw new TypeError("Map window x must be a non-negative safe integer");
      }
      if (!Number.isSafeInteger(value.y) || value.y < 0) {
        throw new TypeError("Map window y must be a non-negative safe integer");
      }
      return Object.freeze({
        version: CONTRACT_VERSION,
        kind: value.kind,
        windowId,
        x: value.x,
        y: value.y,
      });
    }
    default:
      throw new TypeError("Map window layout change has an unsupported kind");
  }
}

function currentPlainObject(value) {
  return isPlainObject(value) ? value : {};
}

function runtimeLayerIds(projection) {
  const layers = Array.isArray(projection.runtime?.catalog?.layers)
    ? projection.runtime.catalog.layers
    : [];
  return new Set(
    layers
      .map((layer) => layer?.layerId)
      .filter((layerId) => typeof layerId === "string" && layerId === layerId.trim() && layerId),
  );
}

function assertKnownLayerId(layerId, knownLayerIds) {
  if (!knownLayerIds.has(layerId)) {
    throw new TypeError(`Unknown map layer identifier: ${layerId}`);
  }
}

function assertKnownLayerIds(layerIds, knownLayerIds) {
  for (const layerId of layerIds) {
    assertKnownLayerId(layerId, knownLayerIds);
  }
}

function sparseOverrideMap(current, key, override) {
  const next = { ...currentPlainObject(current) };
  if (override === null) {
    delete next[key];
  } else if (isPlainObject(override)) {
    next[key] = {
      ...override,
      ...(Array.isArray(override.color) ? { color: override.color.slice() } : {}),
    };
  } else {
    next[key] = override;
  }
  return next;
}

export function mapLayerFilterChangeV1ToSignalPatch(value, projection) {
  const detail = normalizeMapLayerFilterChangeV1(value);
  assertMapShellProjectionV1(projection);
  const knownLayerIds = runtimeLayerIds(projection);
  const filters = currentPlainObject(projection.bridged?.filters);
  if (detail.kind === "visibility") {
    assertKnownLayerIds(detail.visibleLayerIds, knownLayerIds);
    return {
      _map_bridged: {
        filters: { layerIdsVisible: detail.visibleLayerIds.slice() },
      },
    };
  }
  if (detail.kind === "stack") {
    assertKnownLayerIds(detail.orderedLayerIds, knownLayerIds);
    return {
      _map_bridged: {
        filters: {
          layerIdsOrdered: detail.orderedLayerIds.slice(),
          layerClipMasks: { ...detail.clipMaskByLayer },
        },
      },
    };
  }
  if (detail.kind === "trade-npc-classes") {
    return {
      _map_bridged: {
        filters: { tradeNpcClassesVisible: detail.classesVisible.slice() },
      },
    };
  }
  assertKnownLayerId(detail.layerId, knownLayerIds);
  const fieldByKind = {
    opacity: "layerOpacities",
    "point-icon-scale": "layerPointIconScales",
    highlight: "layerHighlights",
    "waypoint-connections": "layerWaypointConnectionsVisible",
    "waypoint-labels": "layerWaypointLabelsVisible",
    "point-icons": "layerPointIconsVisible",
    "point-clustering": "layerPointClusteringEnabled",
    "confidence-showcase": "layerConfidenceShowcase",
  };
  const field = fieldByKind[detail.kind];
  return {
    _map_bridged: {
      filters: {
        [field]: sparseOverrideMap(filters[field], detail.layerId, detail.override),
      },
    },
  };
}

export function mapLayerPresentationChangeV1ToSignalPatch(value, projection) {
  const detail = normalizeMapLayerPresentationChangeV1(value);
  assertMapShellProjectionV1(projection);
  const knownLayerIds = runtimeLayerIds(projection);
  const layers = currentPlainObject(projection.ui?.layers);
  if (detail.kind === "expanded-settings") {
    assertKnownLayerIds(detail.expandedLayerIds, knownLayerIds);
    return {
      _map_ui: {
        layers: { expandedLayerIds: detail.expandedLayerIds.slice() },
      },
    };
  }
  assertKnownLayerId(detail.layerId, knownLayerIds);
  if (detail.kind === "hover-facts") {
    return {
      _map_ui: {
        layers: {
          hoverFactsVisibleByLayer: {
            ...currentPlainObject(layers.hoverFactsVisibleByLayer),
            [detail.layerId]: { ...detail.visibleFacts },
          },
        },
      },
    };
  }
  const field = detail.kind === "lineage"
    ? "lineagePanelVisibleByLayer"
    : "sampleHoverVisibleByLayer";
  const next = { ...currentPlainObject(layers[field]) };
  if (detail.kind === "lineage") {
    if (detail.visible) {
      next[detail.layerId] = true;
    } else {
      delete next[detail.layerId];
    }
  } else if (detail.visible) {
    delete next[detail.layerId];
  } else {
    next[detail.layerId] = false;
  }
  return { _map_ui: { layers: { [field]: next } } };
}

export function mapWindowLayoutChangeV1ToSignalPatch(value, projection) {
  const detail = normalizeMapWindowLayoutChangeV1(value);
  assertMapShellProjectionV1(projection);
  let entryPatch;
  if (detail.kind === "position") {
    entryPatch = { x: detail.x, y: detail.y };
  } else if (detail.kind === "collapsed") {
    entryPatch = { collapsed: detail.collapsed };
  } else {
    entryPatch = { open: detail.open };
  }
  return {
    _map_ui: {
      windowUi: {
        [detail.windowId]: entryPatch,
      },
    },
  };
}

function dispatchMapControlEvent(
  target,
  type,
  detail,
  normalize,
  customEventConstructor = globalThis.CustomEvent,
) {
  const normalizedDetail = normalize(detail);
  if (
    !target
    || typeof target.dispatchEvent !== "function"
    || typeof customEventConstructor !== "function"
  ) {
    return false;
  }
  return target.dispatchEvent(
    new customEventConstructor(type, {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: normalizedDetail,
    }),
  );
}

export function dispatchMapLayerFilterChange(
  target,
  detail,
  customEventConstructor = globalThis.CustomEvent,
) {
  return dispatchMapControlEvent(
    target,
    FISHYMAP_LAYER_FILTER_CHANGE_EVENT,
    detail,
    normalizeMapLayerFilterChangeV1,
    customEventConstructor,
  );
}

export function dispatchMapLayerPresentationChange(
  target,
  detail,
  customEventConstructor = globalThis.CustomEvent,
) {
  return dispatchMapControlEvent(
    target,
    FISHYMAP_LAYER_PRESENTATION_CHANGE_EVENT,
    detail,
    normalizeMapLayerPresentationChangeV1,
    customEventConstructor,
  );
}

export function dispatchMapWindowLayoutChange(
  target,
  detail,
  customEventConstructor = globalThis.CustomEvent,
) {
  return dispatchMapControlEvent(
    target,
    FISHYMAP_WINDOW_LAYOUT_CHANGE_EVENT,
    detail,
    normalizeMapWindowLayoutChangeV1,
    customEventConstructor,
  );
}
